/* Gemians Subscription Buy Box
 *
 * Renders nothing. The visible UI is server-rendered Liquid; this file only
 * wires the tabs to the Kaching Subscriptions widget, which stays in the DOM
 * as the engine that owns the selling_plan input inside the theme's product
 * form. Switching a tab clicks the matching Kaching card, so the cart, the
 * cart drawer and the app's own state all keep working untouched.
 */
(function () {
  'use strict';

  var ROOT = '[data-gem-buybox]';
  var CARD = '.kaching-subscriptions__card-wrapper';
  var ONETIME_CARD = '.kaching-subscriptions__card-wrapper.one-time-purchase';
  var SUB_CARD = '.kaching-subscriptions__card-wrapper:not(.one-time-purchase)';

  var instances = new WeakMap();

  function cardFor(root, kind) {
    return root.querySelector(kind === 'onetime' ? ONETIME_CARD : SUB_CARD);
  }

  function selectedKind(root) {
    var one = root.querySelector(ONETIME_CARD);
    if (one && one.classList.contains('selected')) return 'onetime';
    var sub = root.querySelector(SUB_CARD);
    if (sub && sub.classList.contains('selected')) return 'subscription';
    return null;
  }

  function paint(root, kind) {
    var tabs = root.querySelectorAll('[data-gem-tab]');
    var i;
    for (i = 0; i < tabs.length; i++) {
      var on = tabs[i].getAttribute('data-gem-tab') === kind;
      tabs[i].classList.toggle('is-active', on);
      tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
      tabs[i].tabIndex = on ? 0 : -1;
    }
    var panels = root.querySelectorAll('[data-gem-panel]');
    for (i = 0; i < panels.length; i++) {
      panels[i].hidden = panels[i].getAttribute('data-gem-panel') !== kind;
    }
  }

  /* Only click when the widget disagrees, so a page view never fires a
   * needless interaction at the app. */
  function sync(root, kind) {
    if (selectedKind(root) === kind) return;
    var card = cardFor(root, kind);
    if (card) card.click();
  }

  function activate(root, kind) {
    paint(root, kind);
    sync(root, kind);
  }

  function Instance(root) {
    this.root = root;
    this.kind = root.getAttribute('data-gem-default') === 'onetime' ? 'onetime' : 'subscription';
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.observer = null;
  }

  Instance.prototype.adoptBuyButton = function () {
    var root = this.root;
    var footer = root.querySelector('[data-gem-footer]');
    if (!footer) return;
    var section = root.closest('.shopify-section') || document;
    var form = section.querySelector('product-form');
    if (!form) return;
    var blockEl = form.closest('.shopify-block') || form;
    if (blockEl === root || root.contains(blockEl) === false) {
      if (!footer.contains(blockEl)) footer.appendChild(blockEl);
    }
  };

  /* The widget is the engine, not the interface. Without this it stays in the
   * tab order and the accessibility tree: four focusable cards a keyboard user
   * lands on with nothing visible, and a screen reader reading the whole offer
   * a second time. inert removes both, and programmatic clicks - the thing that
   * actually drives the selling plan - keep working. */
  Instance.prototype.silenceWidget = function () {
    var ks = this.root.querySelector('kaching-subscriptions');
    if (!ks) return;
    if ('inert' in HTMLElement.prototype) {
      ks.inert = true;
    } else if (!ks.hasAttribute('aria-hidden')) {
      ks.setAttribute('aria-hidden', 'true');
    }
  };

  Instance.prototype.onClick = function (e) {
    var tab = e.target.closest ? e.target.closest('[data-gem-tab]') : null;
    if (!tab || !this.root.contains(tab)) return;
    var kind = tab.getAttribute('data-gem-tab');
    if (kind === this.kind) return;
    this.kind = kind;
    activate(this.root, kind);
  };

  Instance.prototype.onKeydown = function (e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    var tab = e.target.closest ? e.target.closest('[data-gem-tab]') : null;
    if (!tab) return;
    var tabs = Array.prototype.slice.call(this.root.querySelectorAll('[data-gem-tab]'));
    var next = tabs[(tabs.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
    if (!next) return;
    e.preventDefault();
    next.focus();
    this.kind = next.getAttribute('data-gem-tab');
    activate(this.root, this.kind);
  };

  /* The app mounts its widget asynchronously. Watch until the cards exist,
   * align them with the tab that is already painted, then stop watching. */
  Instance.prototype.watchWidget = function () {
    var self = this;
    if (this.root.querySelector(CARD)) {
      this.silenceWidget();
      sync(this.root, this.kind);
      return;
    }
    this.observer = new MutationObserver(function () {
      if (!self.root.querySelector(CARD)) return;
      self.observer.disconnect();
      self.observer = null;
      self.silenceWidget();
      sync(self.root, self.kind);
    });
    this.observer.observe(this.root, { childList: true, subtree: true });
  };

  Instance.prototype.start = function () {
    this.adoptBuyButton();
    this.silenceWidget();
    paint(this.root, this.kind);
    this.root.addEventListener('click', this.onClick);
    this.root.addEventListener('keydown', this.onKeydown);
    this.watchWidget();
  };

  Instance.prototype.stop = function () {
    this.root.removeEventListener('click', this.onClick);
    this.root.removeEventListener('keydown', this.onKeydown);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) {
      if (instances.has(roots[i])) continue;
      var inst = new Instance(roots[i]);
      instances.set(roots[i], inst);
      inst.start();
    }
  }

  function teardown(scope) {
    var roots = (scope || document).querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) {
      var inst = instances.get(roots[i]);
      if (!inst) continue;
      inst.stop();
      instances.delete(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });
  document.addEventListener('shopify:block:select', function (e) { init(e.target.closest('.shopify-section') || document); });
})();
